import { Box, InputBase } from '@mui/material'
import type { BoxProps } from '@mui/system'
import type { ChangeEvent, FC, KeyboardEvent } from 'react'
import { useState } from 'react'
import { disableAutocomplete } from '../util/index.js'
import { Tag } from './tag.js'

export interface TagListProps extends Omit<BoxProps, 'onChange'> {
  className?: string
  tags?: string[]
  fullWidth?: boolean
  placeholder?: string
  variant?: 'outlined' | 'filled'
  size?: 'small' | 'medium'
  // If onChange is provided, the input field will be displayed
  onChange?(tags: string[]): void
}

export const TagList: FC<TagListProps> = props => {
  const {
    className,
    tags: defaultTags = [],
    size = 'medium',
    onChange,
    variant = 'outlined',
    fullWidth = true,
    placeholder = 'Add tag...',
    ...restProps
  } = props
  const [name, setName] = useState('')

  const tags = defaultTags ?? []

  const handleDelete = (tag: string) => () => onChange && onChange(tags.filter(el => el !== tag))
  const handleChange = (ev: ChangeEvent<HTMLInputElement>) => setName(ev.target.value)
  const handleReturn = (ev: KeyboardEvent<HTMLInputElement>) => {
    const val: string = ev.currentTarget.value
    if (ev.key === 'Enter' && val) {
      setName('')
      if (tags.includes(val)) {
        return
      }
      if (onChange) {
        onChange([...tags, val.toLowerCase()])
      }
    }
  }

  return (
    <Box
      component={'div'}
      className={className}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        flexWrap: 'wrap',
        fontSize: theme => theme.typography.fontSize * 0.8,
      }}
      {...restProps}
    >
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mr: 1 }}>
        {tags.map(c => (
          <Tag key={c} label={c} size={size} variant={variant} onDelete={onChange ? handleDelete(c) : undefined} />
        ))}
      </Box>
      {onChange && (
        <InputBase
          id="tag-list"
          sx={{
            color: theme => theme.vars.palette.grey[500],
            fontSize: 13,
            whiteSpace: 'nowrap',
          }}
          fullWidth={fullWidth}
          value={name}
          placeholder={placeholder}
          onKeyUp={handleReturn}
          onChange={handleChange}
          inputProps={disableAutocomplete}
          slotProps={{
            input: {
              tabIndex: restProps.tabIndex,
            },
          }}
        />
      )}
    </Box>
  )
}

export default TagList
